import { Component, computed, input, OnInit, output, signal } from '@angular/core';
import { DmInput, DmLabel, DmRichtextEditor } from '@digitale-menschen/component-library-angular';
import type { RichTextToolbarButton } from '@digitale-menschen/component-library';
import { environmentUser } from '@lexplus-environments';
import { RICHTEXT_TOOLBAR_BUTTONS } from '../../models/richtext-toolbar.model';
import { LanguageTab, LanguageTabsComponent, LanguageTabState } from '../language-tabs/language-tabs.component';

/**
 * One language's entry as the editor takes it.
 *
 * Structural on purpose: every API client generates its own `UebersetzungNameBeschreibung` with this shape,
 * and the editor must not belong to any one of them.
 */
export interface UebersetzungNameBeschreibung {
  spracheLokalisierung: string;
  name: string;
  beschreibung?: string | null;
  /** Only edited where {@link UebersetzungenEditNameBeschreibung.showInterneBeschreibung} is on. */
  interneBeschreibung?: string | null;
  hasValues: boolean;
}

/** The Beschreibung's budget, mirrored into the counter under the field. */
const BESCHREIBUNG_MAX_LENGTH = 300;

@Component({
  selector: 'lp-uebersetzungen-edit-name-beschreibung',
  imports: [DmInput, DmLabel, DmRichtextEditor, LanguageTabsComponent],
  templateUrl: './uebersetzungen-edit-name-beschreibung.html',
  styleUrl: './uebersetzungen-edit-name-beschreibung.scss',
})
export class UebersetzungenEditNameBeschreibung implements OnInit {
  public uebersetzungen = input.required<UebersetzungNameBeschreibung[]>();

  /**
   * Marks the Name of **every** language as required, which is what the caller is expected to
   * enforce. Neither requirement is checked here: the editor only shows the asterisk.
   */
  public readonly requiredAll = input<boolean>(false);

  /**
   * Marks Name and Beschreibung as required for **at least one** language. The asterisks go away
   * once one language carries both, since from then on the requirement is met.
   */
  public readonly required = input<boolean>(false);

  /**
   * An error message per language (`spracheLokalisierung` → message) for the Name field, which then
   * renders invalid with the message of the language being edited. The caller owns the check.
   */
  public readonly nameErrors = input<Record<string, string>>({});

  /** Shows the Beschreibung under the Name. Off for an entity that is only named per language. */
  public readonly showBeschreibung = input<boolean>(true);

  /**
   * Leaves the Beschreibung out of the {@link required} requirement: only a Name is needed, so only the Name carries the
   * asterisk, and a language with a Name already meets it.
   */
  public readonly beschreibungOptional = input<boolean>(false);

  /** Shows the Name as a multi-line textarea instead of a single growing line, for names that run long. */
  public readonly multilineName = input<boolean>(false);

  /**
   * The label of the first field, for an entity whose «name» is called something else — «Frage» for
   * a FAQ. Only the wording changes; the field is still the entry's `name`. Falls back to «Name».
   */
  public readonly nameLabel = input<string | undefined>(undefined);

  /** The label of the second field, as {@link nameLabel} is for the first — «Antwort» for a FAQ. Falls back to «Beschreibung». */
  public readonly beschreibungLabel = input<string | undefined>(undefined);

  /** Shows a second textarea under the Beschreibung for text that stays internal. Only for the plain-text Beschreibung. */
  public readonly showInterneBeschreibung = input<boolean>(false);

  /**
   * Edits the Beschreibung in a rich text editor instead of a plain textarea. The editor carries no
   * label of its own, so one is rendered above it, and the character budget is not applied: the
   * value is HTML, whose length says nothing about what the reader sees.
   */
  public readonly richtextBeschreibung = input<boolean>(false);

  /**
   * Turns on pictures in the rich text editor: an «insert image» button, dropping and pasting image
   * files, and drag handles to resize a clicked picture. Opt-in, and only meaningful with
   * {@link richtextBeschreibung}. Needs {@link imageUploadHandler} as well: without one the editor
   * would embed the picture as a base64 blob, which has no business in stored text, so the whole
   * feature stays off instead.
   */
  public readonly allowImages = input<boolean>(false);

  /**
   * Uploads an image the user picked and resolves to the address to put in the `<img src>`. The
   * parent owns the endpoint and the address, so each app points it at the right backend.
   */
  public readonly imageUploadHandler = input<((file: File) => Promise<string>) | undefined>(undefined);

  /** Emitted with the reason when {@link imageUploadHandler} rejects, so the parent can show a toast. */
  public readonly imageUploadFailed = output<unknown>();
  public readonly imageInserted = output<void>();

  public hasChanges = output<boolean>();

  /** The `spracheLokalisierung` of the language the user switched to; not emitted for the initial selection. */
  public readonly languageChanged = output<string>();

  protected activeLanguage = signal<UebersetzungNameBeschreibung | undefined>(undefined);
  protected readonly environmentUser = environmentUser;
  protected readonly beschreibungMaxLength = BESCHREIBUNG_MAX_LENGTH;

  /**
   * Whether the editor gets its image feature — the button, dropping and pasting pictures, and the resize
   * handles. Both inputs are needed, see {@link allowImages}.
   */
  protected readonly imagesEnabled = computed<boolean>(() => this.allowImages() && !!this.imageUploadHandler());

  protected readonly toolbarButtons = computed<RichTextToolbarButton[]>(() =>
    this.imagesEnabled() ? [...RICHTEXT_TOOLBAR_BUTTONS, 'image'] : RICHTEXT_TOOLBAR_BUTTONS,
  );
  protected readonly defaultNameLabel = $localize`:@@common.name:Name`;
  protected readonly defaultBeschreibungLabel = $localize`:@@common.beschreibung:Beschreibung`;
  protected readonly interneBeschreibungLabel = $localize`:@@common.interneBeschreibung:Interne Beschreibung`;

  /**
   * The Beschreibung's current length, tracked separately from {@link activeLanguage}.
   *
   * The entries are edited in place, so that the parent's own object sees the change — which
   * means `activeLanguage.set()` is handed the reference it already holds and notifies
   * nobody. A counter derived from the signal would therefore never move while typing.
   */
  private readonly beschreibungLength = signal<number>(0);

  /** Bumped on every edit, for the same reason: what is derived from the entries would not otherwise re-evaluate. */
  private readonly editRevision = signal<number>(0);

  /** The error of the language being edited; empty when it has none. */
  protected readonly activeNameError = computed<string>(() => this.nameErrors()[this.activeLanguage()?.spracheLokalisierung ?? ''] ?? '');

  /** Whether some language carries a Name and, unless that is optional, a Beschreibung. */
  protected readonly hasCompleteLanguage = computed<boolean>(() => {
    this.editRevision();
    return this.uebersetzungen().some((entry) => !!entry.name?.trim() && (this.beschreibungOptional() || !!entry.beschreibung?.trim()));
  });

  /** The position of the language being edited, for the tab strip. */
  protected readonly activeIndex = computed<number>(() => {
    const active = this.activeLanguage();
    return active ? this.uebersetzungen().indexOf(active) : 0;
  });

  /**
   * Each language's tab. A Name is what every caller needs; where {@link required} asks for a
   * Beschreibung as well (unless {@link beschreibungOptional}), a language with only one of the two is partial. Under {@link requiredAll}
   * the Beschreibung stays optional, so a Beschreibung without a Name is the partial case there.
   */
  protected readonly languageTabs = computed<LanguageTab[]>(() => {
    this.editRevision();
    const needsBeschreibung = this.required() && !this.beschreibungOptional();
    return this.uebersetzungen().map((entry) => {
      const hasName = !!entry.name?.trim();
      const hasBeschreibung = !!entry.beschreibung?.trim();
      const isComplete = hasName && (hasBeschreibung || !needsBeschreibung);
      const state: LanguageTabState = isComplete ? 'complete' : hasName || hasBeschreibung ? 'partial' : 'empty';
      return { sprache: entry.spracheLokalisierung, state };
    });
  });

  /** Whether the «at least one language» requirement is still open, so the asterisks are shown. */
  protected readonly isAnyLanguageRequired = computed<boolean>(() => this.required() && !this.hasCompleteLanguage());

  /** Whether the Beschreibung still carries the asterisk of the «at least one language» requirement. */
  protected readonly isBeschreibungRequired = computed<boolean>(() => this.isAnyLanguageRequired() && !this.beschreibungOptional());

  /** How much of the Beschreibung's budget is left, shown under the field. */
  protected readonly beschreibungCharactersLeft = computed<string>(() => {
    const left = Math.max(0, BESCHREIBUNG_MAX_LENGTH - this.beschreibungLength());
    return $localize`:@@uebersetzung.zeichenUebrig:${left}:count: Zeichen übrig`;
  });

  public ngOnInit(): void {
    this.activateLanguage(this.uebersetzungen()[0]);
  }

  /**
   * Switches the language being edited.
   *
   * @param {number} index - The language's position in {@link uebersetzungen}
   */
  protected selectLanguage(index: number): void {
    const uebersetzung = this.uebersetzungen()[index];
    this.activateLanguage(uebersetzung);
    if (uebersetzung) {
      this.languageChanged.emit(uebersetzung.spracheLokalisierung);
    }
  }

  protected inputChanged(value: string | number | undefined | null, fieldName: string) {
    const data = this.activeLanguage();
    if (data) {
      if (fieldName === 'beschreibung') {
        data.beschreibung = <string>value;
        this.beschreibungLength.set(data.beschreibung?.length ?? 0);
      } else if (fieldName === 'interneBeschreibung') {
        data.interneBeschreibung = <string>value;
      } else if (fieldName === 'name') {
        data.name = <string>value;
      }
      this.activeLanguage.set(data);
      this.updateHasValues();
      this.editRevision.update((revision) => revision + 1);
      this.hasChanges.emit(true);
    }
  }

  private activateLanguage(uebersetzung: UebersetzungNameBeschreibung | undefined): void {
    this.activeLanguage.set(uebersetzung);
    this.beschreibungLength.set(uebersetzung?.beschreibung?.length ?? 0);
  }

  private updateHasValues(): void {
    const data = this.activeLanguage();
    if (!data) {
      return;
    }

    data.hasValues = !!(<string>data.name).length;
    this.activeLanguage.set(data);
  }
}
